import Button from '@jetbrains/ring-ui/components/button/button'
import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import Tooltip from '@jetbrains/ring-ui/components/tooltip/tooltip'
import classNames from 'classnames'
import merge from 'lodash/merge'
import {Suspense, useEffect, useMemo} from 'react'
import {graphql, useFragment, useMutation} from 'react-relay'

import {usePermission} from '../../../../../hooks/usePermission'
import Routes, {getHref} from '../../../../../routes'
import {getBuildStatusTypeFromBuild} from '../../../../../selectors'
import {restApi} from '../../../../../services/rest'
import {Permission} from '../../../../../types'
import {subscribeOnRunningBuild} from '../../../../../utils/subscriber'
import {formatBuildNumber} from '../../../../common/BuildNumber/BuildNumber.utils'
import {
  BranchBadge,
  RunRepositoryBadge as RepositoryBadge,
  RunTimeBadge as AdvancedTimeBadge,
  StatusBadge,
  TriggeredByBadge,
} from '../../components/Badges'
import TimeBadge from '../../components/Badges/TimeBadge/TimeBadge/TimeBadge'
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs'
import {getPipelineRunBreadcrunbs} from '../../components/Breadcrumbs/Breadcrumbs.utils'
import PipelineEditButton from '../../components/PipelineEditButton/PipelineEditButton'
import RunButton from '../../components/RunButton/RunButton'
import headerStyles from '../../EditPipelinePage/EditPipelineHeader/EditPipelineHeader.css'
import {useJobId} from '../../EditPipelinePage/EditPipelinePage.hooks'
import {usePipelineHeadId, usePipelineId} from '../../hooks/pipeline'
import usePipelineDocumentTitle from '../../hooks/usePipelineDocumentTitle'
import {pipelinesApi} from '../../services/pipelinesApi'
import {isNewTimeBadgeEnabled} from '../../utils/featureToggles'
import {usePipelineRunArg} from '../PipelineRunPage.hooks'

import type {PipelineRunHeaderCancelBuildMutation} from './__generated__/PipelineRunHeaderCancelBuildMutation.graphql'
import type {PipelineRunHeaderFragment$key} from './__generated__/PipelineRunHeaderFragment.graphql'

import styles from './PipelineRunHeader.css'

import {useAppDispatch} from 'src/hooks/react-redux'

const fragment = graphql`
  fragment PipelineRunHeaderFragment on ProjectRest {
    rawId
    ...pipelineHeadFragment
  }
`

const cancelBuildMutation = graphql`
  mutation PipelineRunHeaderCancelBuildMutation($buildLocator: String!) {
    cancelBuild(buildLocator: $buildLocator, input: {}) {
      rawId
    }
  }
`

type Props = {pipelineKey: PipelineRunHeaderFragment$key | null}
function PipelineRunHeader({pipelineKey}: Props) {
  const arg = usePipelineRunArg()
  const {run, jobs, parallelized} = restApi.endpoints.getBuildNormalized.useQuery(arg, {
    selectFromResult: ({data}) => {
      const {entities, result} = data ?? {}
      const {builds} = entities ?? {}
      const run = builds?.[result!]
      const jobs = run?.['snapshot-dependencies']?.build
      return {
        run,
        jobs,
        parallelized: jobs?.some(job => job.parallelized),
      }
    },
  })
  const pipelineId = usePipelineId()
  const {name} = pipelinesApi.endpoints.getPipelineById.useQuery(pipelineId, {
    selectFromResult: ({data}) => ({
      name: data?.settings.name,
    }),
  })
  const jobIdMap = useMemo(
    () =>
      jobs?.reduce<Map<number | null | undefined, string | undefined>>(
        (acc, {id, buildType, attributes}) => {
          const buildTypeName = buildType?.name
          const jobIdValue = attributes?.entry?.find(
            entry => entry?.name === 'teamcity.pipeline.job.id',
          )?.value
          const jobId = isNaN(Number(jobIdValue)) ? jobIdValue : buildTypeName

          acc.set(id, jobId)

          return acc
        },
        new Map(),
      ),
    [jobs],
  )
  const {id, number, state, branchName, defaultBranch, triggered} = run ?? {}
  const isQueued = state === 'queued'
  const runNumber = formatBuildNumber({number}).text
  const runStatusType = getBuildStatusTypeFromBuild(run)
  const dispatch = useAppDispatch()
  const currentJobId = Number(useJobId())
  const currentBuildTypeName = jobIdMap?.get(currentJobId)
  const pipeline = useFragment(fragment, pipelineKey)
  const headId = usePipelineHeadId(pipeline)
  const isRunning = state === 'running'

  useEffect(() => {
    if (id != null && isRunning) {
      return subscribeOnRunningBuild(id, data =>
        dispatch(
          restApi.util.updateQueryData('getBuildNormalized', arg, draft => {
            const draftRun = draft?.entities?.builds?.[draft?.result!]

            merge(draftRun, data)
          }),
        ),
      )
    }
    return undefined
  }, [arg, dispatch, id, isRunning])

  usePipelineDocumentTitle(name)

  const [cancelBuild, isCancelling] =
    useMutation<PipelineRunHeaderCancelBuildMutation>(cancelBuildMutation)

  const projectId = pipeline?.rawId
  const canCancel = usePermission(Permission.CANCEL_BUILD, projectId)

  return (
    <div className={styles.header}>
      <div className={styles.breadcrumbs}>
        {name != null && (
          <Breadcrumbs
            path={getPipelineRunBreadcrunbs(pipelineId, name, runNumber, runStatusType)}
          />
        )}
      </div>
      <div className={styles.heading}>
        <H1 className={classNames(headerStyles.title)}>
          {isQueued ? `In queue` : `Run ${runNumber}`}
        </H1>
      </div>
      <div className={styles.actions}>
        {state !== 'finished' && (
          <Tooltip
            title={
              canCancel
                ? undefined
                : `You don't have permission to ${isRunning ? 'stop' : 'cancel'} this run`
            }
          >
            <Button
              className={styles.button}
              onClick={() => cancelBuild({variables: {buildLocator: `id:${id}`}})}
              loader={isCancelling}
              disabled={!canCancel}
            >
              {isRunning ? 'Stop run' : 'Cancel run'}
            </Button>
          </Tooltip>
        )}
        <Suspense>
          <PipelineEditButton
            className={styles.button}
            to={{
              pathname: getHref(Routes.EDIT_PIPELINE, {pipelineId}),
              search: currentBuildTypeName != null ? `job=${currentBuildTypeName}` : ``,
            }}
          />
        </Suspense>
        <RunButton
          primary
          headId={headId!}
          className={styles.button}
          branchName={defaultBranch ? undefined : branchName}
        />
      </div>
      <div className={styles.badges}>
        <StatusBadge status={runStatusType} isRunning={isRunning} />
        {isNewTimeBadgeEnabled ? (
          <AdvancedTimeBadge runId={id} parallelized={parallelized} />
        ) : (
          <TimeBadge runId={id} withOptimizedTime />
        )}
        <BranchBadge branchName={branchName} />
        <TriggeredByBadge triggered={triggered} />
        <RepositoryBadge />
      </div>
    </div>
  )
}

export default PipelineRunHeader
